/**
 * The billing file for the month: ONE CSV of every client's ready
 * lines, from the Invoices rail, under Compile.
 *
 * The per-client download on the Pre-billing tab stays; this is the
 * month-end shape of the same act. It is exportMonthSequentially: one
 * GET per client through the same per-client prebilling route the tab
 * uses, strictly sequential, then one file built by the same row
 * serialisation the tab's download uses (buildBatchPrebillingCsv), so
 * a line reads identically in either file.
 *
 * WHO SEES IT: a seat holding view_invoice AND view_all_clinical, the
 * population that reads the unbilled report. The per-client read is
 * gated on view_invoice alone, but its rows are clinical: a seat
 * without the all-clients width would get a silently SHORTER file,
 * so it gets no panel at all.
 *
 * CANDIDATES: the roster's active, non-demo clients, in roster order.
 * This file leaves demo clients out; the unbilled report and the bulk
 * compile include them.
 *
 * WHAT HAPPENS WHEN A READ FAILS: a 4xx names the client in the report
 * and the file still downloads, short by that client. A 5xx or a dead
 * socket stops the run, names where, and downloads NOTHING, because a
 * file that ends where the connection did looks complete and is not.
 */

import { type ReactNode, useState } from 'react';
import { useTenant } from '@/lib/auth';
import { canViewAllClinical, canViewInvoice } from '@/lib/capabilities';
import { useClientsList } from '@/lib/clientsApi';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useSupportContact } from '@/hooks/useSupportContact';
import { readRateBands } from '@/lib/notes';
import {
  batchPrebillingCsvFilename,
  buildBatchPrebillingCsv,
  monthName,
  readyRowsOf,
} from '@/lib/prebilling';
import { downloadCsvFile } from '@/lib/csv';
import {
  exportMonthSequentially,
  exportRefusedSentence,
  exportStopSentence,
  type ExportMonthReport,
} from '@/lib/billingBatch';
import { Button } from '@/components/primitives/Button';

export interface ExportMonthActionProps {
  /** 'YYYY-MM', the page's picked month: the file holds THE month the
   *  operator is looking at, the same one Compile compiles. */
  month: string;
  /** The month's display name for the button ("August 2026"). */
  monthLabel: string;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Everything the billing-file act knows: who may run it, its
 *  candidates, the run, its progress and its report. The Invoices page
 *  draws the button in its header band and the progress and report in
 *  its body; the run is the same wherever its parts are drawn. */
export interface ExportMonthState {
  visible: boolean;
  monthLabel: string;
  month: string;
  candidateCount: number;
  isLoading: boolean;
  running: boolean;
  progress: { done: number; total: number; monthLabel: string } | null;
  percent: number;
  report: ExportMonthReport | null;
  downloaded: string | null;
  withLinesCount: number;
  run: () => Promise<void>;
}

export function useExportMonth({ month, monthLabel }: ExportMonthActionProps): ExportMonthState {
  const { whoami, tenantSlug } = useTenant();
  const visible = canViewInvoice(whoami) && canViewAllClinical(whoami);
  // The roster read shares the compile picker's query key, so a seat
  // holding both reads it once. A seat that cannot see the panel fires
  // no request.
  const { clients, isLoading } = useClientsList(tenantSlug, { enabled: visible });
  const { data: tenantConfig } = useTenantConfig(tenantSlug, { enabled: visible });
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; monthLabel: string } | null>(
    null,
  );
  const [report, setReport] = useState<ExportMonthReport | null>(null);
  /** The name of the file this run handed the browser, or null when the
   *  run made no file (nothing ready, or a stop). */
  const [downloaded, setDownloaded] = useState<string | null>(null);

  const candidates = clients
    .filter((c) => c.is_active && !c.is_demo)
    .map((c) => ({ id: c.id, display_name: c.display_name }));
  const bands = readRateBands(tenantConfig?.config);

  const run = async (): Promise<void> => {
    setRunning(true);
    setReport(null);
    setDownloaded(null);
    setProgress({ done: 0, total: candidates.length, monthLabel });
    const result = await exportMonthSequentially(tenantSlug, month, candidates, {
      onProgress: (done, total) => setProgress({ done, total, monthLabel }),
    });
    const withLines = result.entries.filter((e) => readyRowsOf(e.rows).length > 0);
    if (result.stopped === null && withLines.length > 0) {
      const filename = batchPrebillingCsvFilename(month);
      downloadCsvFile(filename, buildBatchPrebillingCsv(result.entries, bands));
      setDownloaded(filename);
    }
    setReport(result);
    setRunning(false);
    setProgress(null);
  };

  const withLinesCount =
    report === null ? 0 : report.entries.filter((e) => readyRowsOf(e.rows).length > 0).length;
  const percent = progress && progress.total > 0
    ? Math.round((progress.done / progress.total) * 100)
    : 0;

  return {
    visible,
    monthLabel,
    month,
    candidateCount: candidates.length,
    isLoading,
    running,
    progress,
    percent,
    report,
    downloaded,
    withLinesCount,
    run,
  };
}

export function ExportMonthRunButton({
  state,
  onDark = false,
}: {
  state: ExportMonthState;
  /** On the forest band: the on-dark secondary in every month (LP-39:
   *  Pre-billing and Payroll hours are the band's filled buttons). */
  onDark?: boolean;
}): ReactNode {
  if (!state.visible) return null;
  return (
    <Button
      size="sm"
      variant={onDark ? 'secondary-on-dark' : 'primary'}
      data-testid="invoices-export-run"
      onClick={() => void state.run()}
      loading={state.running}
      disabled={state.running || state.isLoading || state.candidateCount === 0}
    >
      {`Download billing CSV for ${state.monthLabel}`}
    </Button>
  );
}

/** The bar while the file is being prepared; nothing otherwise. */
export function ExportMonthProgress({ state }: { state: ExportMonthState }): ReactNode {
  const { running, progress, percent } = state;
  if (!state.visible || !running || !progress) return null;
  return (
    <div data-testid="invoices-export-progress" className="w-full min-w-0 space-y-2 text-xs text-ink-soft">
      <p id="invoices-export-progress-label" role="status" className="break-words">
        {`Preparing the ${progress.monthLabel} billing CSV`}
      </p>
      <div className="flex w-full items-center gap-2">
        <progress
          aria-labelledby="invoices-export-progress-label"
          max={100}
          value={percent}
          className="h-2 w-full min-w-0 appearance-none overflow-hidden rounded-pill bg-sage-200 [&::-webkit-progress-bar]:bg-sage-200 [&::-webkit-progress-value]:bg-sage-800 [&::-moz-progress-bar]:bg-sage-800"
        />
        <span className="w-[4ch] shrink-0 text-right tabular-nums">{percent}%</span>
      </div>
    </div>
  );
}

/** The last run's report; nothing before a run. */
export function ExportMonthReportView({ state }: { state: ExportMonthState }): ReactNode {
  const contact = useSupportContact();
  const { report, downloaded, withLinesCount, month } = state;
  if (!state.visible || report === null) return null;
  return (
    <div data-testid="invoices-export-report" className="space-y-1">
      <p data-testid="invoices-export-summary" className="text-sm text-ink">
        {`${plural(report.entries.length, 'client read', 'clients read')} · ${withLinesCount} with billable lines · ${report.refused.length} refused`}
      </p>

      {downloaded !== null && (
        <p data-testid="invoices-export-file" className="text-xs text-ink-soft">
          {`Downloaded ${downloaded}.`}
        </p>
      )}

      {downloaded === null && report.stopped === null && (
        <p data-testid="invoices-export-empty" className="text-xs text-ink-soft">
          {`Nothing is ready for ${monthName(month)} yet, so there is no file.`}
        </p>
      )}

      {report.refused.length > 0 && (
        <div data-testid="invoices-export-refused" className="mt-2 space-y-1">
          <h4 className="text-xs font-semibold text-sage-800">
            {`${report.refused.length} refused`}
          </h4>
          <ul className="space-y-1">
            {report.refused.map(({ client, reason }) => (
              <li
                key={client.id}
                data-testid={`invoices-export-refused-${client.id}`}
                className="text-xs text-ink-soft"
              >
                <span className="font-medium text-ink">{client.display_name}</span>{' '}
                {exportRefusedSentence(reason, contact)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.stopped !== null && (
        <p data-testid="invoices-export-stopped" className="mt-2 text-xs text-pepper-deep">
          {exportStopSentence(report.stopped)}
        </p>
      )}
    </div>
  );
}

export function ExportMonthAction(props: ExportMonthActionProps): ReactNode {
  const state = useExportMonth(props);
  if (!state.visible) return null;
  return (
    <section
      data-testid="invoices-export-panel"
      className="space-y-2 border border-sage-200 bg-sage-50 rounded-card p-4"
    >
      <h2 className="font-display text-lg text-sage-800">Billing file for the month</h2>
      <p className="text-xs text-ink-soft">
        One CSV of every client&rsquo;s ready lines, the same columns as a client&rsquo;s own
        download.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <ExportMonthRunButton state={state} />
        <ExportMonthProgress state={state} />
      </div>
      <ExportMonthReportView state={state} />
    </section>
  );
}
